/**
 * Drawing helpers on the 2D stand-in that the star, the sun, the network and
 * the places share: a soft glow, and a ray of light.
 */
import type { Canvas2D } from './renderer/renderer'

const SPIKE_SOFT = 0.55 /* how soft a spike's edges are (higher = wider, hazier falloff to the sides) */
const SPRITE_WIDTH = 512 /* the ray sprite's size in pixels */
const SPRITE_HEIGHT = 64
const SPRITE_EXTENT = 1.65 /* sprite half-height = SPRITE_EXTENT × the ray's width */

const sprites = new Map<string, HTMLCanvasElement>()

/** A radial glow: `rgb` ("r,g,b") at the centre, fading through a warm tint at `inner` (0..1 of the radius) to nothing. */
export function glow(
  ctx: Canvas2D,
  x: number,
  y: number,
  radius: number,
  alpha: number,
  inner: number,
  rgb = '255,252,244'
) {
  if (alpha < 0.004 || radius < 0.3) return
  const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius)
  gradient.addColorStop(0, 'rgba(' + rgb + ',' + alpha.toFixed(3) + ')')
  gradient.addColorStop(inner, 'rgba(246,226,192,' + (alpha * 0.3).toFixed(3) + ')')
  gradient.addColorStop(1, 'rgba(201,164,112,0)')
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, 6.2832)
  ctx.fill()
}

/**
 * A ray of light: a soft gaussian streak, brightest at the star and fading to
 * its tip, narrowing as it goes, pre-rendered once per colour as a sprite and
 * then just rotated and scaled. No hard edges anywhere.
 */
function raySprite(rgb: string): HTMLCanvasElement {
  const cached = sprites.get(rgb)
  if (cached) return cached
  const canvas = document.createElement('canvas')
  canvas.width = SPRITE_WIDTH
  canvas.height = SPRITE_HEIGHT
  const g = canvas.getContext('2d')!
  const image = g.createImageData(SPRITE_WIDTH, SPRITE_HEIGHT)
  const data = image.data
  const colour = rgb.split(',').map(Number)
  const sigma0 = (SPIKE_SOFT / SPRITE_EXTENT) * (SPRITE_HEIGHT / 2)
  for (let x = 0; x < SPRITE_WIDTH; x++) {
    const u = (x + 0.5) / SPRITE_WIDTH
    const along =
      Math.pow(1 - u, 1.25) * Math.exp(-1.7 * u) /* bright at the star, gone at the tip */
    const sigma = sigma0 * (1 - 0.8 * u) /* narrowing as it goes */
    for (let y = 0; y < SPRITE_HEIGHT; y++) {
      const v = (y + 0.5 - SPRITE_HEIGHT / 2) / sigma
      const a = along * Math.exp(-0.5 * v * v)
      const o = (y * SPRITE_WIDTH + x) * 4
      data[o] = colour[0]
      data[o + 1] = colour[1]
      data[o + 2] = colour[2]
      data[o + 3] = Math.min(255, a * 255 + Math.random() - 0.5)
    }
  }
  g.putImageData(image, 0, 0)
  sprites.set(rgb, canvas)
  return canvas
}

/** One ray from (`cx`, `cy`) at `angle`, `length` long and `width` wide at the core. */
export function spike(
  ctx: Canvas2D,
  cx: number,
  cy: number,
  angle: number,
  length: number,
  width: number,
  rgb: string,
  alpha: number
) {
  if (alpha < 0.003 || length < 1) return
  const h = width * SPRITE_EXTENT
  ctx.save()
  ctx.translate(cx, cy)
  ctx.rotate(angle)
  ctx.globalAlpha = Math.min(alpha, 1)
  ctx.drawImage(raySprite(rgb), 0, -h, length, 2 * h)
  ctx.restore()
}
